import React from "react";
import { FaMagic, FaRedo, FaRegCopy } from "react-icons/fa";

const URGENCY = {
  now: { label: "Act now", tone: "critical" },
  this_week: { label: "This week", tone: "at_risk" },
  this_month: { label: "This month", tone: "watch" },
  monitor: { label: "Monitor", tone: "healthy" },
};

const OWNER_LABELS = {
  customer_success: "Customer success",
  sales: "Sales",
  support: "Support",
  product: "Product",
};

/** Plain-text version of a recommendation, for pasting into Slack or a CRM note. */
export function recommendationToText(result) {
  if (!result) return "";
  const { recommendation, organizationName } = result;
  const lines = [
    `${organizationName} — ${recommendation.headline}`,
    "",
    recommendation.assessment,
    "",
    "Next actions:",
    ...(recommendation.nextActions || []).map(
      (action, index) =>
        `${index + 1}. [${OWNER_LABELS[action.owner] || action.owner}] ${action.action}\n   Why: ${action.why}`,
    ),
  ];

  if (recommendation.talkingPoints?.length) {
    lines.push("", "Talking points:", ...recommendation.talkingPoints.map((p) => `- ${p}`));
  }
  if (recommendation.watchFor?.length) {
    lines.push("", "Watch for:", ...recommendation.watchFor.map((p) => `- ${p}`));
  }

  return lines.join("\n");
}

/**
 * The AI half of a client drill-in.
 *
 * Generated on click, never on load: it costs a model call, and a table of 300
 * clients that quietly ran 300 of them would be indefensible. Every action
 * shows the evidence it was built from, so a recommendation that drifted from
 * the data is visible rather than merely plausible.
 */
export function RecommendationPanel({ state, onGenerate }) {
  const { loading, error, result } = state || {};
  const urgency = result ? URGENCY[result.recommendation.urgency] || URGENCY.monitor : null;

  const copy = () => {
    navigator.clipboard?.writeText(recommendationToText(result));
  };

  return (
    <section className="ct-detail-panel ct-detail-panel--ai">
      <header className="ct-detail-head">
        <h4>
          <FaMagic aria-hidden="true" /> What should we do?
        </h4>
        <div className="ct-detail-head-actions">
          {result ? (
            <button type="button" className="ct-linkbtn" onClick={copy}>
              <FaRegCopy aria-hidden="true" /> Copy
            </button>
          ) : null}
          <button
            type="button"
            className="ct-btn ct-btn--ai"
            onClick={onGenerate}
            disabled={loading}
          >
            {loading ? (
              "Thinking…"
            ) : result ? (
              <>
                <FaRedo aria-hidden="true" /> Regenerate
              </>
            ) : (
              <>
                <FaMagic aria-hidden="true" /> Ask Addy
              </>
            )}
          </button>
        </div>
      </header>

      <div className="ct-ai">
        {!result && !loading && !error ? (
          <p className="ct-crm-note">
            Generates a recommendation from this client's signals, usage history
            and HubSpot record. Costs one model call, so it only runs when you
            ask.
          </p>
        ) : null}

        {loading ? (
          <div className="ct-ai-loading">
            <span className="ct-skel" style={{ width: "70%" }} />
            <span className="ct-skel" style={{ width: "95%" }} />
            <span className="ct-skel" style={{ width: "88%" }} />
          </div>
        ) : null}

        {error ? <p className="ct-ai-error">{error}</p> : null}

        {result && !loading ? (
          <>
            <div className="ct-ai-headline">
              <span className={`ct-urgency ct-urgency--${urgency.tone}`}>{urgency.label}</span>
              <h5>{result.recommendation.headline}</h5>
            </div>
            <p className="ct-ai-assessment">{result.recommendation.assessment}</p>

            <ol className="ct-ai-actions">
              {result.recommendation.nextActions.map((action, index) => (
                <li key={index}>
                  <div className="ct-ai-action-head">
                    <span className="ct-ai-owner">
                      {OWNER_LABELS[action.owner] || action.owner}
                    </span>
                    <span className="ct-ai-action">{action.action}</span>
                  </div>
                  <p className="ct-ai-why">{action.why}</p>
                </li>
              ))}
            </ol>

            {result.recommendation.talkingPoints?.length ? (
              <div className="ct-ai-block">
                <h6>Talking points</h6>
                <ul>
                  {result.recommendation.talkingPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result.recommendation.watchFor?.length ? (
              <div className="ct-ai-block">
                <h6>Watch for</h6>
                <ul>
                  {result.recommendation.watchFor.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="ct-ai-foot">
              Generated from the {result.windowDays}-day window and this client's{" "}
              {result.signals?.length || 0} detected signal
              {result.signals?.length === 1 ? "" : "s"}. Check the numbers before acting.
            </p>
          </>
        ) : null}
      </div>
    </section>
  );
}
